import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface DashboardMetrics {
  readonly openObservations: number;
  readonly closedObservations: number;
  readonly criticalRisks: number;
  readonly complianceScore: number;
  readonly controlEffectiveness: number;
  readonly auditCoverage: number;
  readonly capaStatus: { readonly open: number; readonly closed: number; readonly overdue: number };
  readonly pendingReviews: number;
}

export function M21AuditDashboardPage(): ReactNode {
  const { number } = useBusinessNumber();
  const { t } = useTranslation();
  const { token } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: DashboardMetrics }>('/v1/audit/dashboard', { token })
      .then((r) => { if (alive) setMetrics(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  if (error) return <p role="alert">{error}</p>;
  if (!metrics) return <p>{t('m21.auditDashboard.loading')}</p>;
  return (
    <main aria-label="audit-dashboard">
      <h1>{t('m21.auditDashboard.title')}</h1>
      <section aria-label="dashboard-metrics" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
        <article aria-label="open-observations"><h2>{t('m21.auditDashboard.openObservations')}</h2><p>{metrics.openObservations}</p></article>
        <article aria-label="closed-observations"><h2>{t('m21.auditDashboard.closedObservations')}</h2><p>{metrics.closedObservations}</p></article>
        <article aria-label="critical-risks"><h2>{t('m21.auditDashboard.criticalRisks')}</h2><p>{metrics.criticalRisks}</p></article>
        <article aria-label="compliance-score"><h2>{t('m21.auditDashboard.complianceScore')}</h2><p>{number(metrics.complianceScore)}%</p></article>
        <article aria-label="control-effectiveness"><h2>{t('m21.auditDashboard.controlEffectiveness')}</h2><p>{metrics.controlEffectiveness}%</p></article>
        <article aria-label="audit-coverage"><h2>{t('m21.auditDashboard.auditCoverage')}</h2><p>{metrics.auditCoverage}%</p></article>
        <article aria-label="capa-open"><h2>{t('m21.auditDashboard.capaOpen')}</h2><p>{metrics.capaStatus.open}</p></article>
        <article aria-label="capa-overdue"><h2>{t('m21.auditDashboard.capaOverdue')}</h2><p>{metrics.capaStatus.overdue}</p></article>
        <article aria-label="pending-reviews"><h2>{t('m21.auditDashboard.pendingReviews')}</h2><p>{metrics.pendingReviews}</p></article>
      </section>
    </main>
  );
}
